import React, { useState, useMemo } from 'react';
import { MapPin, Globe, Download, Filter, Eye, EyeOff } from 'lucide-react';
import { generateGeographicData, exportChart } from '../utils/visualization';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface GeographicCoverageMapProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

interface CoverageData {
  companyId: string;
  companyName: string;
  coverage: {
    primary_markets: string[];
    total_cities: number;
    total_states: number;
    international: boolean;
    coverage_density: 'dense' | 'moderate' | 'sparse';
    year_updated: number;
    source: string;
  };
  color: string;
}

export const GeographicCoverageMap: React.FC<GeographicCoverageMapProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(
    new Set(['thumbtack', 'angi', 'taskrabbit', 'porch_group'])
  );
  const [viewMode, setViewMode] = useState<'coverage' | 'density' | 'international'>('coverage');

  const data = useMemo(() => generateGeographicData(), []);
  const filteredData = data.filter(item => 
    (!filters.company || item.companyId === filters.company) &&
    selectedCompanies.has(item.companyId)
  );

  const toggleCompany = (companyId: string) => {
    const newSelection = new Set(selectedCompanies);
    if (newSelection.has(companyId)) {
      newSelection.delete(companyId);
    } else {
      newSelection.add(companyId);
    }
    setSelectedCompanies(newSelection);
  };

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('geographic-coverage-map');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'geographic-coverage-map',
      includeData: true,
    };

    await exportChart(config, chartElement, data);
  };

  // US States for visualization (simplified)
  const usStates = [
    { name: 'California', code: 'CA', x: 10, y: 60 },
    { name: 'Texas', code: 'TX', x: 40, y: 80 },
    { name: 'Florida', code: 'FL', x: 80, y: 90 },
    { name: 'New York', code: 'NY', x: 85, y: 30 },
    { name: 'Pennsylvania', code: 'PA', x: 80, y: 35 },
    { name: 'Illinois', code: 'IL', x: 55, y: 40 },
    { name: 'Ohio', code: 'OH', x: 70, y: 40 },
    { name: 'Georgia', code: 'GA', x: 75, y: 75 },
    { name: 'North Carolina', code: 'NC', x: 80, y: 65 },
    { name: 'Michigan', code: 'MI', x: 65, y: 30 },
    { name: 'Washington', code: 'WA', x: 15, y: 20 },
    { name: 'Arizona', code: 'AZ', x: 25, y: 75 },
    { name: 'Massachusetts', code: 'MA', x: 90, y: 30 },
    { name: 'Colorado', code: 'CO', x: 35, y: 55 },
    { name: 'Oregon', code: 'OR', x: 12, y: 35 },
  ];

  const densityColors = {
    dense: '#059669',
    moderate: '#d97706',
    sparse: '#dc2626',
  };

  const getDensityIntensity = (company: CoverageData, state: string) => {
    // Simulate coverage intensity based on company data
    const baseIntensity = company.coverage.coverage_density === 'dense' ? 0.8 :
                         company.coverage.coverage_density === 'moderate' ? 0.5 : 0.3;
    
    // Major markets get higher intensity
    const isMajorMarket = company.coverage.primary_markets.some(market => 
      state.toLowerCase().includes(market.toLowerCase()) ||
      market.toLowerCase().includes(state.toLowerCase())
    );
    
    return isMajorMarket ? Math.min(baseIntensity + 0.2, 1) : baseIntensity;
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Geographic Coverage Map</h2>
          <p className="text-sm text-gray-600 mt-1">
            Service availability and market penetration
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('coverage')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'coverage'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Coverage
            </button>
            <button
              onClick={() => setViewMode('density')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'density'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Density
            </button>
            <button
              onClick={() => setViewMode('international')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'international'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Global
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Company Selection */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-sm font-medium text-gray-700">Companies:</span>
        {data.map((company) => (
          <button
            key={company.companyId}
            onClick={() => toggleCompany(company.companyId)}
            className={`flex items-center gap-2 px-3 py-1 text-sm rounded-md border transition-colors ${
              selectedCompanies.has(company.companyId)
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-gray-50 border-gray-200 text-gray-500'
            }`}
          >
            {selectedCompanies.has(company.companyId) ? (
              <Eye className="h-3 w-3" />
            ) : (
              <EyeOff className="h-3 w-3" />
            )}
            {company.companyName}
          </button>
        ))}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Focus Company</label>
              <select
                value={filters.company || ''}
                onChange={(e) => onFilterChange?.({ ...filters, company: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Companies</option>
                {data.map(company => (
                  <option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">View Mode</label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as 'coverage' | 'density' | 'international')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="coverage">Coverage Map</option>
                <option value="density">Density Heatmap</option>
                <option value="international">International Presence</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Map Visualization */}
      <div id="geographic-coverage-map" className="relative">
        {viewMode === 'international' ? (
          /* International View */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredData.map((company) => (
              <div key={company.companyId} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: company.color }}
                  />
                  <h3 className="text-lg font-medium text-gray-900">{company.companyName}</h3>
                  {company.coverage.international && (
                    <Globe className="h-5 w-5 text-green-600" />
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Primary Markets:</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {company.coverage.primary_markets.map((market) => (
                        <span 
                          key={market}
                          className="px-2 py-1 text-xs bg-white rounded border"
                        >
                          {market}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Cities:</span>
                      <span className="ml-2 font-medium">
                        {company.coverage.total_cities || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">States:</span>
                      <span className="ml-2 font-medium">
                        {company.coverage.total_states || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: densityColors[company.coverage.coverage_density] }}
                      />
                      <span className="text-sm text-gray-700 capitalize">
                        {company.coverage.coverage_density} Coverage
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* US Map View */
          <div className="relative">
            {/* Simplified US Map Background */}
            <svg
              viewBox="0 0 100 80"
              className="w-full h-96 bg-gray-100 rounded-lg border"
            >
              {/* US States as circles for simplicity */}
              {usStates.map((state) => {
                return filteredData.map((company, companyIndex) => {
                  const intensity = getDensityIntensity(company, state.name);
                  const radius = viewMode === 'density' ? intensity * 3 : 2;
                  const opacity = viewMode === 'density' ? intensity : 0.7;
                  
                  return (
                    <g key={`${state.code}-${company.companyId}`}>
                      <circle
                        cx={state.x + (companyIndex * 2) - 2}
                        cy={state.y + (companyIndex * 2) - 2}
                        r={radius}
                        fill={company.color}
                        opacity={opacity}
                        stroke="#fff"
                        strokeWidth="0.5"
                      />
                      {companyIndex === 0 && (
                        <text
                          x={state.x}
                          y={state.y - 5}
                          fontSize="2"
                          textAnchor="middle"
                          fill="#374151"
                        >
                          {state.code}
                        </text>
                      )}
                    </g>
                  );
                });
              })}
            </svg>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {viewMode === 'density' ? 'Coverage Intensity' : 'Market Presence'}
                </span>
              </div>
              {viewMode === 'density' && (
                <div className="flex items-center gap-4">
                  {Object.entries(densityColors).map(([density, color]) => (
                    <div key={density} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm text-gray-700 capitalize">{density}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Coverage Statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm font-medium text-blue-900 mb-1">Total Markets</div>
          <div className="text-2xl font-bold text-blue-900">
            {[...new Set(data.flatMap(d => d.coverage.primary_markets))].length}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm font-medium text-green-900 mb-1">International</div>
          <div className="text-2xl font-bold text-green-900">
            {data.filter(d => d.coverage.international).length}
          </div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <div className="text-sm font-medium text-amber-900 mb-1">Dense Coverage</div>
          <div className="text-2xl font-bold text-amber-900">
            {data.filter(d => d.coverage.coverage_density === 'dense').length}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm font-medium text-purple-900 mb-1">Avg Cities</div>
          <div className="text-2xl font-bold text-purple-900">
            {Math.round(data.reduce((sum, d) => sum + (d.coverage.total_cities || 0), 0) / data.length)}
          </div>
        </div>
      </div>
    </div>
  );
};